import TextField from "@material-ui/core/TextField";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import AlertDialog from "../../components/AlertDialog";
import ScheduleIcon from "@material-ui/icons/Schedule";
import ClassNames from "classnames";
import _ from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router";
import Accordion from "../../components/Accordion";
import AiSummaryBox from "../../components/AiSummaryBox";
import Button from "../../components/Button";
import NotesSidebar from "../../components/NotesSidebar";
import PageContent from "../../components/PageContent";
import ReviewDateSummary from "../../components/ReviewDateSummary";
import Section from "../../components/Section";
import SectionTitle from "../../components/SectionTitle";
import SessionBasicInfo from "../../components/SessionBasicInfo";
import SessionSummary from "../../components/SessionSummary";
import Stepper from "../../components/Stepper";
import SubjectListWithTarget from "../../components/SubjectListWithTarget";
import { AiSummaryStar } from "../../config/Icons";
import { IsSessionReadOnly } from "../../config/Roles";
import { useNotification } from "../../context/notification";
import { DashboardLayout } from "../../layouts/DashboardLayout";
import GetSessionData from "../../services/getSessionData";
import { apiRequest } from "../../utils/api";
import { getLocalValue } from "../../utils/auth";
import { specialCharacterCheck } from "../../utils/customValidation";
import { utcToLocal } from "../../utils/datetime";
import styles from "./SessionsReviewSession.module.scss";

export function SessionsReviewSession() {
  const history = useHistory();
  const { addNotification } = useNotification();
  const { id } = useParams();
  const { t } = useTranslation();
  const [rating, setRating] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);
  const [aiSummary, setAiSummary] = useState("");
  const [isAiSummaryError, setIsAiSummaryError] = useState(false);
  const [summaryLogId, setSummaryLogId] = useState(null);
  const [isAiCoachingEnabled, setIsAiCoachingEnabled] = useState(false);
  const [sessionDetails, setSessionDetails] = useState({});
  const [checkpoints, setCheckpoints] = useState([]);
  const [growFormDetails, setGrowFormDetails] = useState({});
  const [subjectList, setSubjectList] = useState([]);
  const [sessionDuration, setSessionDuration] = useState(0);
  const [finalData, setFinalData] = useState([]);
  const [sessionSummary, setSessionSummary] = useState("");
  const [sessionSummaryError, setSessionSummaryError] = useState(false);
  const [specialCharacterError, setSpecialCharacterError] = useState(false);
  const [overwriteReasonError, setOverwriteReasonError] = useState(false);
  const [replaceDialogOpen, setReplaceDialogOpen] = useState(false);

  const intialForm = {
    sessionSummary: "",
  };
  if (!localStorage.sessionSummaryData) {
    localStorage.setItem("sessionSummaryData", JSON.stringify(intialForm));
  }

  const localData =
    localStorage.sessionSummaryData &&
    localStorage.sessionSummaryData !== undefined
      ? JSON.parse(localStorage.sessionSummaryData)
      : {};

  useEffect(() => {
    setTimeout(() => {
      setSessionSummary(localData.sessionSummary);
    }, 2000);
  }, []);

  useEffect(() => {
    // get session details
    async function fetchSessionData() {
      setIsLoading(true);
      const sessionAPIData = await GetSessionData(id);
      if (sessionAPIData) {
        setSessionDetails(sessionAPIData);
        setSubjectList(sessionAPIData.subject);
        // AI Coaching pilot region gate - the API also rejects summaries for
        // non-pilot regions with a 403 (COACH-2152).
        const aiEnabled = Boolean(sessionAPIData.ai_coaching_enabled);
        setIsAiCoachingEnabled(aiEnabled);
        if (aiEnabled) fetchSummary();
      } else {
        addNotification(
          `Could not get session details from id #${id}`,
          "error"
        );
      }
      setIsLoading(false);
    }

    fetchSessionData();

    // get checkpoint details
    apiRequest
      .get(`/api/getCheckpointDetails?session_id=${id}`)
      .then(response => {
        const data = response.data.data;
        if (data && data.progress_data) {
          setCheckpoints(data.progress_data);
        }
        if (data && data.final_data) {
          setFinalData(data.final_data);
        }
      })
      .catch(error => {
        console.error(error);
      });
  }, []);

  // helper function to access values within session details
  const getSessionValue = key => {
    return sessionDetails.session ? sessionDetails.session[key] : undefined;
  };

  // get current session duration for the stepper
  const getTotalTime = time => {
    setSessionDuration(time);
  };

  // auxillary function to submit overwritePass for a single subject the user is overwriting
  function submitOverwritePass({
    sessionID,
    subjectID,
    reasonText,
    successCallback = t => {},
    failureCallback = t => {},
  }) {
    let paramObj = {
      session_id: sessionID,
      subject_id: subjectID,
      overwrite_pass: 1,
      updated_by: getLocalValue("uid"),
      reason_overwrite_pass: reasonText,
    };
    apiRequest
      .post(`/api/overWritePass`, paramObj)
      .then(resp => {
        successCallback(resp);
      })
      .catch(error => {
        failureCallback(error);
      })
      .finally(() => {});
  }

  function submitSessionReviewSummary() {
    let paramObj = {
      session_id: id,
      duration: sessionDuration, // TODO: submit duration here rayees!
      created_by: getLocalValue("uid"),
      summary: sessionSummary,
    };
    apiRequest
      .post(`/api/saveSessionReviewSummary`, paramObj)
      .then(resp => {
        if (resp.status === 200) {
          addNotification(
            t("reviewSession.sessionReviewSummarySavedSuccessfully"),
            "success"
          );
          localStorage.removeItem("sessionSummaryData");
          history.push(`/sessions/review-summary/${id}`);
        }
      })
      .catch(error => {})
      .finally(() => {});
  }

  const fetchSummary = () => {
    setIsSummaryLoading(true);
    setIsAiSummaryError(false);
    apiRequest
      .post(
        `/api/ai/evaluate-summary`,
        { sessionId: id },
        { showLoader: false, skipNotification: true }
      )
      .then(resp => {
        if (resp.status === 200 && resp.data && resp.data.result.summary) {
          setAiSummary(resp.data.result.summary);
          setSummaryLogId(resp.data.result.logId);
        }
      })
      .catch(error => {
        setIsAiSummaryError(true);
      })
      .finally(() => {
        setIsSummaryLoading(false);
      });
  };

  const handleConfirm = () => {
    if (sessionSummary === "") {
      setSessionSummaryError(true);
      return;
    }
    if (!specialCharacterCheck(sessionSummary)) {
      setSessionSummaryError(true);
      setSpecialCharacterError(true);
      return;
    }
    let pending = []; // array to store subject_ids whose API calls are still pending
    let overwriteError = []; // array to store subject_ids whose API calls are still pending
    finalData.forEach(o => {
      if (o.overwrite_pass === 1) {
        if (o.reason_overwrite_pass === "") {
          overwriteError.push("error");
          return;
        }
        if (!specialCharacterCheck(o.reason_overwrite_pass)) {
          overwriteError.push("error");
          return;
        }

        pending.push(o.subject_id);

        // calls overwritePass API for each subject the user is overwriting
        submitOverwritePass({
          sessionID: id,
          subjectID: o.subject_id,
          reasonText: o.reason_overwrite_pass,
          successCallback: resp => {
            let index = pending.indexOf(o.subject_id);
            if (index !== -1) {
              pending.splice(index, 1); // removes subject_id from pending array when done
            }
          },
          failureCallback: err => {
            addNotification(`${err}`, "error");
          },
        });
      }
    });
    if (overwriteError.length > 0) {
      setOverwriteReasonError(true);
      return;
    }
    // periodically checks for pending array to be empty
    const pendingCheck = setInterval(() => {
      if (pending.length <= 0) {
        clearInterval(pendingCheck); // clears self
        submitSessionReviewSummary();
      }
    }, 500);

    // timeout for pendingCheck after 10 seconds
    setTimeout(() => {
      if (pending.length > 0) {
        let pendings = "";
        pending.forEach((n, index) => {
          pendings += `${n}${index + 1 < pending.length ? ", " : ""}`;
        });

        clearInterval(pendingCheck);
        addNotification(
          `Failed to overwrite pass for subject${
            pending.length > 1 ? "s" : ""
          } ${pendings}`,
          "error"
        );
      }
    }, 10000);
  };

  const handleSentimentChange = newValue => {
    apiRequest
      .post(
        `/api/ai-summary-logs/sentiment`,
        { logId: summaryLogId, sentiment: newValue },
        { showLoader: false }
      )
      .then(resp => {
        if (resp.status === 200) {
          addNotification(t("reviewSession.feedbackSubmitted"), "success");
        }
      })
      .catch(error => {
        addNotification(t("reviewSession.failedToSendFeedback"), "error");
      });
  };

  const handleLike = () => {
    handleSentimentChange("positive");
  };

  const handleDislike = () => {
    handleSentimentChange("negative");
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(aiSummary);
    addNotification(t("reviewSession.summaryCopiedToClipboard"), "success");
  };

  // Applies the AI summary to the coach's summary field and localStorage
  const applyAiSummary = () => {
    setSessionSummary(aiSummary);
    const tmp = JSON.parse(localStorage.sessionSummaryData);
    tmp.sessionSummary = aiSummary;
    localStorage.setItem("sessionSummaryData", JSON.stringify(tmp));
  };

  // Check if summary field already has content before inserting
  const handleInsert = () => {
    if (sessionSummary && sessionSummary.trim().length > 0) {
      setReplaceDialogOpen(true);
    } else {
      // no existing text insert directly
      applyAiSummary();
    }
  };

  // Confirmed replace from dialog
  const handleConfirmReplace = () => {
    applyAiSummary();
    setReplaceDialogOpen(false);
  };

  // Cancelled replace from dialog
  const handleCancelReplace = () => {
    setReplaceDialogOpen(false);
  };

  const isReadOnly = useMemo(() => {
    if (_.isEmpty(sessionDetails) || !sessionDetails.session) return true;
    return IsSessionReadOnly(
      sessionDetails.session.coach_id,
      sessionDetails.session.coachee_id
    );
  }, [sessionDetails]);

  return (
    <DashboardLayout
      appBarContent={
        <Stepper
          phase={t("header.stepper.act")}
          phaseLabel="Act"
          sessionId={id}
          clockRun={!isReadOnly}
          getTotalTimeSpent={getTotalTime}
        />
      }
      rightSidebarContent={
        <NotesSidebar
          sessionID={id}
          isCoach={
            sessionDetails["coach_id"] === parseInt(getLocalValue("uid"))
          }
          accessible={!isReadOnly}
        />
      }
      contentClass={styles.dashboardLayout__content}
    >
      <div className={styles.pageWrapper}>
        <PageContent indent>
          <Section>
            <SectionTitle>{t("reviewSession.reviewSession")}</SectionTitle>
            <div className={styles.detailsContainer}>
              <div className={styles.sessionDetailsBlock}>
                <SessionBasicInfo sessionData={sessionDetails.session} />
                <div>
                  <Accordion
                    summary={t("sessionSummary.viewSessionSummary")}
                    details={<SessionSummary id={id}></SessionSummary>}
                  />

                  <ReviewDateSummary id={id} />
                </div>
              </div>
              <div className={styles.sessionFinalReviewBlock}>
                <div
                  className={ClassNames(
                    styles.sessionGrowFormBlock,
                    styles.borderTop
                  )}
                >
                  <div className={styles.row}>
                    <div className={styles.rowLeft}>
                      <div className={styles.labelTitle}>
                        {t("reviewSession.finalReview")}
                      </div>
                    </div>
                    <div className={styles.rowRight}>
                      <div className={styles.content}>
                        <div className={styles.dateTime}>
                          <div className={styles.date}>
                            <CalendarTodayIcon className={styles.icon} />{" "}
                            <span>
                              {utcToLocal(
                                getSessionValue("review_datetime")
                              ).format("D MMM YYYY")}
                            </span>
                          </div>
                          <div className={styles.time}>
                            <ScheduleIcon className={styles.icon} />{" "}
                            <span>
                              {utcToLocal(
                                getSessionValue("review_datetime")
                              ).format("h:mm A")}
                            </span>
                          </div>
                        </div>
                        <SubjectListWithTarget
                          list={
                            subjectList && subjectList.length > 0
                              ? subjectList
                              : []
                          }
                          finalData={
                            finalData && finalData.length > 0 ? finalData : null
                          }
                          setFinalData={setFinalData}
                          hideCurrent
                          allowOverwriting
                          isReadOnly={isReadOnly}
                        />
                        {overwriteReasonError && (
                          <div className={styles.error}>
                            Please add Reason for overwriting. <br />
                            special characters are not allowed
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  {isAiCoachingEnabled && (
                    <div className={styles.row}>
                      <div className={styles.rowLeft}>
                        <div className={styles.aiTitle}>
                          <AiSummaryStar className={styles.aiIcon} />
                          {t("reviewSession.aiSummary")}
                        </div>
                        <div className={styles.aiSubtitle}>
                          {t("reviewSession.aiSubtitle")}
                        </div>
                      </div>
                      <div className={styles.rowRight}>
                        <AiSummaryBox
                          summary={aiSummary}
                          isLoading={isSummaryLoading}
                          isError={isAiSummaryError}
                          handleTryAgain={fetchSummary}
                          handleLike={handleLike}
                          handleDislike={handleDislike}
                          handleCopy={handleCopy}
                          handleInsert={handleInsert}
                        />
                      </div>
                    </div>
                  )}
                  <div className={styles.row}>
                    <div className={styles.rowLeft}>
                      <div className={styles.labelTitle}>
                        {" "}
                        {t("reviewSession.coachSummary")}
                      </div>
                    </div>
                    <div className={styles.rowRight}>
                      <TextField
                        className={styles.textarea}
                        multiline
                        rows={4}
                        placeholder={t("reviewSession.coachSummaryPlaceholder")}
                        inputProps={{ style: { resize: "both" } }}
                        variant="outlined"
                        error={sessionSummaryError}
                        disabled={isReadOnly}
                        helperText={
                          sessionSummaryError && specialCharacterError
                            ? "Special Characters are not allowed"
                            : sessionSummaryError &&
                              "Summary is field is required"
                        }
                        onChange={e => {
                          setSessionSummary(e.target.value);
                          setSessionSummaryError(
                            e.target.value !== "" ? false : true
                          );
                          if (localStorage.sessionSummaryData) {
                            const tmp = JSON.parse(
                              localStorage.sessionSummaryData
                            );
                            tmp.sessionSummary = e.target.value;
                            localStorage.setItem(
                              "sessionSummaryData",
                              JSON.stringify(tmp)
                            );
                          }
                        }}
                        value={sessionSummary}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className={styles.confirmButtonWrapper}>
                <Button
                  variant="outlined"
                  color="primary"
                  fullWidth
                  onClick={handleConfirm}
                  isloading={isLoading ? 1 : 0}
                  disabled={isReadOnly}
                >
                  {t("reviewSession.proceedToSummary")}
                </Button>
              </div>
            </div>
          </Section>
        </PageContent>
      </div>
      {/* Replace summary confirmation dialog */}
      <AlertDialog
        open={replaceDialogOpen}
        onClose={handleCancelReplace}
        title={t("reviewSession.replaceSummaryTitle")}
        body={t("reviewSession.replaceSummaryBody")}
        footer={
          <>
            <Button onClick={handleCancelReplace}>{t("common.cancel")}</Button>
            <Button
              onClick={handleConfirmReplace}
              variant="contained"
              style={{ backgroundColor: "#d32f2f", color: "#fff" }}
            >
              {t("reviewSession.replace")}
            </Button>
          </>
        }
      />
    </DashboardLayout>
  );
}
